import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export interface DatabricksModelService {
  /** Fully-qualified Unity Catalog model service, for example `system.ai.gpt-oss-20b`. */
  name: string;
  comment?: string;
  creator?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface DatabricksModelServiceDiscoveryOptions {
  /** Bounded page size. Databricks rejects some unbounded model-service listings. Default 100. */
  pageSize?: number;
  /** Pagination safety limit. Default 100. */
  maxPages?: number;
  /** Restrict discovery to this UC prefix. Default `system.ai.`. */
  requiredPrefix?: string;
}

export interface DatabricksModelProviderService {
  name: string;
  providerType: string;
  targets: string[];
  allowAllTargets: boolean;
}

interface ModelServiceResponse {
  model_services?: Array<Record<string, unknown>>;
  next_page_token?: string;
}

interface GatewayEndpointsResponse {
  endpoints?: unknown[];
  next_page_token?: string;
}

/**
 * Verify that Unity AI Gateway v2 is enabled. A successful empty listing still means the service is
 * available; model-service discovery reports whether usable models exist.
 */
export async function ensureDatabricksAiGateway(
  client: DatabricksRawProtocolClient,
): Promise<void> {
  await client.get<GatewayEndpointsResponse>("/api/ai-gateway/v2/endpoints", { page_size: 1 });
}

/** List fully-qualified Unity Catalog model services with bounded, loop-safe pagination. */
export async function listDatabricksModelServices(
  client: DatabricksRawProtocolClient,
  options: DatabricksModelServiceDiscoveryOptions = {},
): Promise<DatabricksModelService[]> {
  const pageSize = positiveInteger(options.pageSize ?? 100, "pageSize");
  const maxPages = positiveInteger(options.maxPages ?? 100, "maxPages");
  const requiredPrefix = options.requiredPrefix ?? "system.ai.";
  const services = new Map<string, DatabricksModelService>();
  const seenTokens = new Set<string>();
  let pageToken: string | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const response = await client.get<ModelServiceResponse>(
      "/api/2.1/unity-catalog/model-services",
      { page_size: pageSize, ...(pageToken ? { page_token: pageToken } : {}) },
    );
    for (const raw of response.model_services ?? []) {
      const service = normalizeModelService(raw, requiredPrefix);
      if (service) services.set(service.name, service);
    }
    const next = response.next_page_token?.trim();
    if (!next) return [...services.values()].sort((a, b) => a.name.localeCompare(b.name));
    if (seenTokens.has(next))
      throw new Error(
        `databricks model-services pagination repeated token after page ${page + 1}.`,
      );
    seenTokens.add(next);
    pageToken = next;
  }
  throw new Error(`databricks model-services pagination exceeded ${maxPages} pages.`);
}

/** List governed external-model providers registered as Unity Catalog securables. */
export async function listDatabricksModelProviderServices(
  client: DatabricksRawProtocolClient,
): Promise<DatabricksModelProviderService[]> {
  const payload = await client.get<{
    model_provider_services?: Array<{
      name?: string;
      config?: {
        provider_type?: string;
        targets?: Array<{ model?: string }>;
        allow_all_targets?: boolean;
      };
    }>;
  }>("/api/2.1/unity-catalog/model-provider-services");
  return (payload.model_provider_services ?? [])
    .flatMap((service) => {
      const rawName = service.name?.trim();
      if (!rawName) return [];
      const config = service.config ?? {};
      return [
        {
          name: rawName.replace(/^model-provider-services\//, ""),
          providerType: (config.provider_type ?? "")
            .replace(/^EXTERNAL_MODEL_PROVIDER_TYPE_/, "")
            .toLowerCase(),
          targets: (config.targets ?? [])
            .map((target) => target.model?.trim())
            .filter((model): model is string => Boolean(model)),
          allowAllTargets: Boolean(config.allow_all_targets),
        },
      ];
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function databricksModelProviderSupportsAnthropic(
  service: DatabricksModelProviderService,
): boolean {
  return service.providerType === "anthropic" || service.providerType === "amazon_bedrock";
}

function normalizeModelService(
  raw: Record<string, unknown>,
  requiredPrefix: string,
): DatabricksModelService | undefined {
  const rawName = typeof raw.name === "string" ? raw.name.trim() : "";
  const name = rawName.replace(/^model-services\//, "");
  if (!name.startsWith(requiredPrefix)) return undefined;
  return {
    name,
    ...(typeof raw.comment === "string" ? { comment: raw.comment } : {}),
    ...(typeof raw.creator === "string" ? { creator: raw.creator } : {}),
    ...(typeof raw.created_at === "number" ? { createdAt: raw.created_at } : {}),
    ...(typeof raw.updated_at === "number" ? { updatedAt: raw.updated_at } : {}),
  };
}

function positiveInteger(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new Error(`${name} must be a positive integer.`);
  return value;
}
